import { AdvancedSearchQuery } from '../../../../components/TableSearch/types';

// Display name source indicator — mirrors api/constants/products.js DISPLAY_NAME_SOURCE
export const DISPLAY_NAME_SOURCE = {
  USER: 'user',
  ORANGE_BOOK: 'orange_book',
  NDC: 'ndc',
  CAS: 'cas',
  MANUFACTURER: 'manufacturer',
} as const;

export type DisplayNameSource =
  (typeof DISPLAY_NAME_SOURCE)[keyof typeof DISPLAY_NAME_SOURCE];

/**
 * A single Orange Book product record returned by GET /api/products/:id.
 * A product can legitimately link to several Orange Book products (e.g.
 * brand + generic) — the endpoint returns all of them, ordered RLD
 * preferred then most-recent data_file_date first (issue #622, Sub-task 5.1;
 * was previously restricted to the top 1 row at both the query and panel
 * layers).
 */
export interface OBRecord {
  fda_product_id: number;
  trade_name: string | null;
  ingredient: string | null;
  applicant_full_name: string | null;
  appl_type: string | null;
  appl_no: string | null;
  approval_date: string | null;
  te_code: string | null;
  strength: string | null;
  df: string | null;
  route: string | null;
}

/**
 * Single USDMF (Drug Master File) record linked to a product, returned by
 * GET /api/products/:id (issue #522). A product can legitimately link to
 * several DMF filings — same unbounded shape as OBRecord now uses too
 * (issue #622, Sub-task 5.1).
 *
 * holder/submit_date added in issue #622 Sub-task 5.2 — both columns already
 * exist on dmf.DMF_Products and are populated on import, just weren't
 * selected. holder is NOT NULL; submit_date is nullable.
 */
export interface DMFRecord {
  dmf_number: number;
  dmf_type: string | null;
  status: string | null;
  subject: string | null;
  holder: string;
  submit_date: string | null;
  /**
   * `dmf.DMF_Products.dmf_product_id` — the surrogate id the External Source
   * Linking break-link API (`DELETE /api/external-links/:productId/dmf/:recordId`,
   * issue #622 Task 6) requires as `recordId`. Declared here (optional) for
   * Task 8's break-link UI to type against, but the Task 5 read path
   * (`api/sql/products.js`'s `dmf_records` query, ~line 2340) does not
   * currently select it — only `dmf_number` (the natural/provenance key, NOT
   * the surrogate id used for the API path). Until that one-line SQL
   * addition lands, this is always `undefined` and DMFPanel hides its
   * break-link icon accordingly rather than sending a wrong id. See the
   * Task 8 session report for this known gap.
   */
  dmf_product_id?: number;
}

// An NDC listing linked to this product (ndc.NDC_Products via ndc.Products_NDC).
// No dosage-form/route fields exist for NDC (unlike OBRecord) — the source
// table simply doesn't carry them.
export interface NDCRecord {
  ndc_product_id: number;
  product_ndc: string | null;
  generic_name: string | null;
  labeler_name: string | null;
  product_type: string | null;
  finished: boolean | null;
  active_ingredients: string | null;
  marketing_start_date: string | null;
  dea_schedule: string | null;
  listing_expiration_date: string | null;
  marketing_end_date: string | null;
  data_file_date: string | null;
}

// A confirmed, agreeing CAS source row (issue #350, FR-7) — one per
// (product, source), never a conflicting/blocked result (AD-10 agree-invariant).
export interface CasSourceRow {
  source: string;
  match_basis: 'cas_exact' | 'name_r1' | 'name_r2';
  cas_number: string;
  source_native_id: string | null;
  molecular_formula: string | null;
  molecular_weight: string | null;
  smiles: string | null;
  inchi: string | null;
  inchi_key: string | null;
  queried_at: string;
  synonyms: string[];
}

// One candidate offered by a source, as carried in a review-queue row's
// blocked_payload.candidates (issue #350, FR-7).
export interface CasReviewCandidate {
  source: string;
  cas_number: string | null;
  cas_candidates?: string[];
  source_native_id?: string | null;
  primary_name?: string | null;
  names?: string[];
  molecular_formula?: string | null;
  molecular_weight?: string | null;
  smiles?: string | null;
  inchi?: string | null;
  inchi_key?: string | null;
}

// A pending Product_Cas_Review_Queue row a human can confirm or reject
// (issue #350, FR-7) — only ambiguous_name/unconfirmed_candidate reasons are
// ever surfaced here; blocked_* reasons stay queried-directly-via-SQL (AD-11).
export interface CasReviewQueueEntry {
  review_id: number;
  reason: 'ambiguous_name' | 'unconfirmed_candidate';
  sources_involved: string | null;
  blocked_payload: { candidates: CasReviewCandidate[] } | null;
  status: 'pending';
  detected_at: string;
}

// Response shape of the confirm/reject CAS-candidate endpoints (issue #350, FR-7).
export interface CasCandidateActionResponse {
  message: string;
  product_id: number;
  review_id: number;
}

// ---------------------------------------------------------------------------
// External Source Linking — break-link preview/commit (issue #622, Task 8.3).
// Mirrors docs/API.md's "External Source Linking" section and
// api/sql/externalLinkConsequences.js's `computeBreakLinkConsequences`
// return shape exactly, since the `comparable` object is echoed back
// verbatim as `expected_consequences` on the DELETE call (Architecture
// Decision 19) — these types are NOT reconstructed/simplified client-side.
// ---------------------------------------------------------------------------

export type ExternalLinkSource = 'orange_book' | 'ndc' | 'dmf';

export interface ExternalLinkNameVariantRemoved {
  name_id: number;
  name: string;
  normalized_name: string;
}

export interface ExternalLinkSourceFlag {
  column: string;
  will_flip_to_false: boolean;
}

export interface ExternalLinkPreferredNameMatch {
  matches: boolean;
  preferred_product_name: string | null;
}

export interface ExternalLinkDependentDrop {
  source: string;
  record_id: number;
  match_basis: string;
  display_name: string | null;
}

/**
 * The opaque `comparable` consequence projection — echoed back verbatim as
 * `expected_consequences` on `DELETE /api/external-links/:productId/:source/:recordId`.
 */
export interface ExternalLinkConsequences {
  link_exists: boolean;
  name_variant_removed: ExternalLinkNameVariantRemoved | null;
  source_flag: ExternalLinkSourceFlag | null;
  preferred_name_match: ExternalLinkPreferredNameMatch;
  dependent_links_dropped: ExternalLinkDependentDrop[];
}

export interface PreviewBreakLinkResponse {
  product_id: number;
  source: ExternalLinkSource;
  record_id: number;
  computed_at: string;
  comparable: ExternalLinkConsequences;
}

export interface BreakLinkResponse {
  product_id: number;
  source: ExternalLinkSource;
  record_id: number;
  deleted: boolean;
  tombstone_created: boolean;
  rejection_id: number | null;
  consequences_applied: ExternalLinkConsequences;
}

/** Response body of a 409 from the break-link DELETE (Architecture Decision 19). */
export interface BreakLinkConflictBody {
  error: 'consequences_changed';
  message: string;
  previous_consequences: ExternalLinkConsequences;
  current_consequences: ExternalLinkConsequences;
}

// ---------------------------------------------------------------------------
// External Source Linking — search (Task 7.1) and link-create (Task 6.2),
// backing the "Link to Source" modal (issue #622, Task 9). Field lists below
// mirror `displayColumns` per source in `api/sql/externalLinkSources.js`
// exactly — nothing here is a fabricated/invented field.
// ---------------------------------------------------------------------------

/** Fields common to every source's search result row (docs/API.md "Search and suggestions (Task 7)"). */
export interface ExternalLinkSearchResultBase {
  record_id: number;
  matched_field: string;
  matched_value: string;
  /**
   * True when this record is already linked to the product the search was
   * scoped to (issue #681, item 2) — search intentionally still RETURNS
   * already-linked records (no exclusion, unlike suggestions), this just
   * flags which ones they are so the admin isn't confused into thinking
   * they're new candidates. Always `false` when the search wasn't scoped to
   * a product (`ProductService.searchExternalLinkSource` called without a
   * `productId`).
   */
  is_linked: boolean;
}

/** One `fda.OrangeBook_Products` row, per Task 6.1's `orange_book.displayColumns`. */
export interface OBSearchResult extends ExternalLinkSearchResultBase {
  source: 'orange_book';
  ingredient: string | null;
  trade_name: string | null;
  applicant_full_name: string | null;
  appl_type: string | null;
  appl_no: string | null;
  product_no: string | null;
  approval_date: string | null;
  te_code: string | null;
  strength: string | null;
  df: string | null;
  route: string | null;
  data_file_date: string | null;
}

/** One `ndc.NDC_Products` row, per Task 6.1's `ndc.displayColumns`. */
export interface NDCSearchResult extends ExternalLinkSearchResultBase {
  source: 'ndc';
  product_ndc: string | null;
  generic_name: string | null;
  labeler_name: string | null;
  product_type: string | null;
  finished: boolean | null;
  active_ingredients: string | null;
  marketing_start_date: string | null;
  dea_schedule: string | null;
  listing_expiration_date: string | null;
  marketing_end_date: string | null;
  data_file_date: string | null;
}

/** One `dmf.DMF_Products` row, per Task 6.1's `dmf.displayColumns`. */
export interface DMFSearchResult extends ExternalLinkSearchResultBase {
  source: 'dmf';
  dmf_number: number;
  dmf_type: string | null;
  status: string | null;
  subject: string | null;
  holder: string;
  submit_date: string | null;
  data_file_date: string | null;
}

/**
 * A single search result row, discriminated on `source`. NOTE: the raw
 * `GET /api/external-link-search/:source` response (docs/API.md) does not
 * carry `source` on each individual result — only once, at the response's
 * top level, since one call is always for one source. `ProductService
 * .searchExternalLinkSource` stamps `source` onto every row before
 * returning, so client code gets a real discriminated union to switch on —
 * both for display (Sub-task 9.2/9.4) and for the review list's
 * `(source, record_id)` dedupe key (Sub-task 9.4) — instead of threading a
 * separately-tracked source value through every call site.
 */
export type ExternalLinkSearchResult =
  OBSearchResult | NDCSearchResult | DMFSearchResult;

export interface ExternalLinkSearchResponse {
  source: ExternalLinkSource;
  query: string;
  page: number;
  page_size: number;
  total: number;
  results: ExternalLinkSearchResult[];
}

/** `POST /api/external-links/:productId` request body (Task 6.2). */
export interface CreateExternalLinksRequest {
  records: Array<{ source: ExternalLinkSource; record_id: number }>;
}

export interface CreatedExternalLink {
  source: ExternalLinkSource;
  record_id: number;
  tombstone_cleared: boolean;
}

export interface AlreadyLinkedExternalLink {
  source: ExternalLinkSource;
  record_id: number;
}

export interface CreateExternalLinksResponse {
  product_id: number;
  linked: CreatedExternalLink[];
  already_linked: AlreadyLinkedExternalLink[];
}

/**
 * One grouped candidate match within a single variant's suggestion response
 * (Sub-task 9.3), per `docs/API.md`'s "Search and suggestions (Task 7)"
 * section and `scoreCandidatesAgainstVariant`
 * (`api/sql/externalLinkSearch.js`).
 *
 * `records` (issue #622 follow-up) carries one entry per `record_ids`
 * member with that source's full `displayColumns` detail — the same field
 * names `ExternalLinkSearchResultBase`'s subtypes (`OBSearchResult` etc.)
 * use, deliberately typed loosely (`Record<string, unknown>` plus the two
 * fields every entry always has) rather than re-declaring the exact
 * discriminated shape here, since which fields are present is entirely
 * driven by the source's `displayColumns` config on the API side, not by
 * this type. See `externalLinkRecordAdapter.ts`'s
 * `toExternalLinkSearchResultFromSuggestion` for how a group's `record_ids`
 * are staged into the review list using this real detail (no fabricated
 * placeholder fields).
 */
export interface ExternalLinkSuggestionResult {
  matched_field: string;
  matched_variant: string;
  display_name: string;
  score: number;
  record_count: number;
  record_ids: number[];
  records: Array<
    { record_id: number; matched_field: string } & Record<string, unknown>
  >;
}

/**
 * `GET /api/external-link-search/:productId/suggestions/:source` response
 * (Sub-task 7.2) for ONE `variantIndex` — see the streaming-vs-fallback note
 * in `api/routes/externalLinkSearch.js`'s header. `variant`/`total_variants`
 * are server-owned (the client never computes or de-dupes the variant list
 * itself); `display_floor`/`candidate_cap` are echoed so the client never
 * hardcodes Task 4's tunable constants (Decision 14).
 */
export interface ExternalLinkSuggestionsResponse {
  product_id: number;
  source: ExternalLinkSource;
  variant_index: number;
  variant: string | null;
  total_variants: number;
  display_floor: number;
  candidate_cap: number;
  results: ExternalLinkSuggestionResult[];
}

/** `POST /api/external-links/:productId` 422 pre-validation-failure body (Task 6.2/docs/API.md). */
export interface CreateExternalLinksValidationFailedBody {
  error: 'validation_failed';
  message: string;
  invalid_records: Array<{
    source: ExternalLinkSource;
    record_id: number;
    reason: string;
  }>;
}

/** `POST /api/external-links/:productId` 400 request-shape-error body (Task 6.2/docs/API.md). */
export interface CreateExternalLinksBadRequestBody {
  error: 'validation_error';
  message: string;
  record?: { source: string; record_id: number };
}

/**
 * One record staged for linking in the "Link to Source" modal's review list
 * (Sub-task 9.4), before the Sub-task 9.5 confirmation actually commits it.
 * `addedVia` distinguishes 9.2's search checkboxes from 9.3's (not yet
 * built) suggestion checkboxes — both push into the SAME list, using the
 * same `(source, record_id)` dedupe key (see
 * `domains/products/adapters/externalLinkRecordAdapter.ts`'s
 * `getStagedRecordKey`), so 9.3 slots in without reshaping this type.
 */
export interface StagedExternalLinkRecord {
  source: ExternalLinkSource;
  record_id: number;
  /** Exactly what Task 7's search result (or, later, 9.3's suggestion
   * lookup) already returned — never a fabricated display shape. */
  record: ExternalLinkSearchResult;
  addedVia: 'search' | 'suggestion';
}

export interface Product {
  product_id: number;
  product_cas_name: string;
  preferred_product_name?: string | null;
  cas_number?: string | null;
  note?: string | null;
  user_id: string;
  created_date: Date;
  modified_date: Date;
  uri?: string | null;
  img?: string | null;
  inchi?: string | null;
  inchi_key?: string | null;
  smile?: string | null;
  canonical_smile?: string | null;
  molecular_formula?: string | null;
  molecular_mass?: string | null;
  is_cas_verified?: boolean;
  is_fda_linked?: boolean;
  is_ndc_linked?: boolean;
  is_dmf_linked?: boolean;
  ob_product_name?: string | null;
  synonyms?: string[];
  replaced_rns?: string[];
  // Materialized column on dbo.Products, trigger-maintained from dbo.Product_Names
  // (AD-16 precedence: user-set -> Orange Book -> NDC -> CAS -> manufacturer)
  display_name?: string | null;
  display_name_source?: DisplayNameSource | null;
  // Orange Book records returned by the detail endpoint (all links, RLD
  // preferred then most-recent data_file_date first — issue #622 Sub-task 5.1)
  ob_records?: OBRecord[];
  // USDMF records linked to this product (issue #522)
  dmf_records?: DMFRecord[];
  // NDC listings linked to this product (External Sources NDC panel)
  ndc_records?: NDCRecord[];
  // Multi-source CAS provenance (issue #350, FR-7/T6) — additive; the flat
  // fields above (cas_number, is_cas_verified, inchi, etc.) remain the
  // resolved/canonical values for backward-compat.
  cas_sources?: CasSourceRow[];
  cas_candidates?: CasReviewQueueEntry[];
  // Per-source CAS official names (issue #585 Task 9.1) — sourced from
  // dbo.Product_Names, a different table than CasSourceRow's
  // Product_Cas_Source_Data. Surfaced in CASRegistryPanel's SourceBlock.
  cas_official_common_chemistry?: string | null;
  cas_official_pubchem?: string | null;
  cas_official_fda_gsrs?: string | null;
  manufacturers: ManufacturerSummary[];
  facilities: FacilitySummary[];
  grades: ManufacturerWithGrade[];
  therapeutic_areas: TherapeuticAreaSummary[];
}

export interface ManufacturerWithGrade {
  manufacturer_id: number;
  manufacturing_status: string | null;
  grade_id: number | null;
  grade_name?: string | null;
}

export interface ManufacturerSummary {
  manufacturer_id: number;
  manufacturer_name: string;
  manufacturing_status?: ManufacturingStatus | null;
  grade_ids?: number[];
  man_product_name?: string | null;
}

export interface FacilitySummary {
  facility_id: number;
  facility_name: string;
  country_code?: string | null;
  comments?: string | null;
}

// ===== FLAT PRODUCT TYPES =====
// Used for MUI DataGrid row grouping (denormalized Cartesian product)

/**
 * Flat denormalized product row
 * Each row represents one unique combination of:
 * Product × Manufacturer × Facility × Grade × Therapeutic Area
 *
 * Uses composite ID format: "p{id}_m{id}_f{id}_g{id}_ta{id}"
 * NULL relationships represented as "mNULL", "fNULL", etc.
 */
export interface FlatProduct {
  // Composite ID (unique for each row in Cartesian product)
  id: string; // e.g., "p8132_m456_f789_g12_ta5" or "p8132_mNULL_fNULL_gNULL_taNULL"

  // Product fields
  product_id: number;
  product_cas_name: string;
  preferred_product_name?: string | null;
  cas_number?: string | null;
  is_cas_verified?: boolean;
  is_fda_linked?: boolean;
  is_ndc_linked?: boolean;
  is_dmf_linked?: boolean;
  note?: string | null;
  user_id: string;
  created_date: Date;
  modified_date: Date;
  uri?: string | null;
  img?: string | null;
  inchi?: string | null;
  inchi_key?: string | null;
  smile?: string | null;
  canonical_smile?: string | null;
  molecular_formula?: string | null;
  molecular_mass?: string | null;
  // Per-source CAS official names (issue #585 Task 4) — scalar TOP-1 subqueries
  // scoped to one source_system each, superseding the retired product_cas_name
  cas_official_common_chemistry?: string | null;
  cas_official_pubchem?: string | null;
  cas_official_fda_gsrs?: string | null;

  // Manufacturer fields (nullable - can be NULL for products without manufacturers)
  manufacturer_id: number | null;
  manufacturer_name: string | null;
  manufacturer_short_name?: string | null;
  manufacturing_status?: ManufacturingStatus | null;

  // Facility fields (nullable - can be NULL for manufacturers without facilities)
  facility_id: number | null;
  facility_name: string | null;
  facility_city?: string | null;
  facility_status?: string | null;
  country_code?: string | null;

  // Grade fields (nullable - can be NULL for products without grades)
  // Per-manufacturer: each flat row carries the grade assigned to THIS product+manufacturer pair
  // via Products_Manufacturers_Grades. Values differ per manufacturer when a product is
  // supplied in different grades by different manufacturers.
  grade_id: number | null;
  grade_name: string | null;

  // Therapeutic Area fields (nullable - can be NULL for products without therapeutic areas)
  therapeutic_area_id: number | null;
  therapeutic_area_name: string | null;
  therapeutic_area_description?: string | null;

  // Orange Book Product Name — read from dbo.Product_Names (orange_book_ingredient); NULL if no OB link
  ob_product_name?: string | null;

  // Computed display name: AD-16 precedence (user-set -> Orange Book -> NDC -> CAS -> manufacturer)
  // This is the value returned in the preferred_product_name column of the flat SELECT
  display_name?: string | null;

  // Source indicator for the computed display name
  display_name_source?: DisplayNameSource | null;

  // True multi-value fields (issue #585 Task 7/8) — attached by
  // attachMultiValueFields() as a post-query grouping pass, not part of the
  // main SELECT's own columns; always present (empty array, never
  // undefined) once queryProductsFlat() returns.
  orange_book_ingredient: string[];
  ndc_active_ingredient: string[];
  manufacturer: string[];
  synonyms: string[];
  replaced_rns: string[];
  // Index-paired — dmf_number[i]/dmf_type[i] describe the same linked DMF
  // record (issue #585 Task 10.1).
  dmf_number: string[];
  dmf_type: string[];

  // Facility comments for this row's facility (issue #585 Task 10.2) —
  // dbo.Facilities.comments, not a Products column; already selected, not
  // part of the multi-value grouping pass above.
  comments?: string | null;
}

/**
 * Query response for flat products
 * Returns denormalized Cartesian product rows for MUI DataGrid row grouping
 */
export interface FlatProductQueryResponse {
  products: FlatProduct[];
  total: number; // Total rows after Cartesian product (for backwards compatibility)
  totalProducts: number; // Total distinct products (for proper pagination)
}

/**
 * Query response for GET /products/ids (issue #637, VPE-010, Endpoint 7) —
 * IDs only, lighter payload than FlatProductQueryResponse for callers (the
 * validate-products devtool modal) that don't need full Cartesian rows.
 */
export interface ProductIdsResponse {
  product_ids: number[];
}

export interface GradeSummary {
  grade_id: number;
  grade_name: string;
}

export interface TherapeuticAreaSummary {
  therapeutic_area_id: number;
  therapeutic_area_name: string;
}

export type ManufacturingStatus = 'Commercial' | 'R&D';

export interface CreateProductRequest extends Pick<
  Product,
  'product_cas_name' | 'cas_number' | 'note'
> {
  manufacturer_ids: number[];
  facility_ids: number[];
  grade_ids: number[];
  therapeutic_area_ids: number[];
}

export interface UpdateProductRequest extends Pick<
  Product,
  | 'product_id'
  | 'product_cas_name'
  | 'preferred_product_name'
  | 'cas_number'
  | 'note'
  | 'uri'
  | 'img'
  | 'inchi'
  | 'inchi_key'
  | 'smile'
  | 'canonical_smile'
  | 'molecular_formula'
  | 'molecular_mass'
  | 'synonyms'
  | 'replaced_rns'
> {
  manufacturers?: Array<{
    manufacturer_id: number;
    manufacturing_status?: ManufacturingStatus | null;
    grade_ids?: number[];
    man_product_name?: string | null;
  }>;
  manufacturer_ids?: number[]; // Deprecated: kept for backward compatibility
  facility_ids?: number[];
  grade_ids?: number[];
  therapeutic_area_ids?: number[];
  selectedCasRn?: string; // For multi-result pick-one re-PUT (Task 4.2)
}

export interface ProductResponse extends Product {}

// ─── PUT /products/:id response shapes (Task 4.2) ──────────────────────────

/** Normal save: product was updated. `warning` present on CasNoResult downgrade. */
export interface PutProductSuccess {
  message: string;
  product_id: number;
  warning?: string;
  status?: never; // discriminant: excludes this from conflict / multi_result checks
}

/** Verified product: name resolves to a DIFFERENT single CAS entity. Save blocked. */
export interface PutProductConflict {
  status: 'conflict';
  conflictingEntity: { rn: string; name: string };
  orphanCount: number;
}

/** A single CAS candidate returned by the registry. */
export interface CasCandidate {
  cas_number: string;
  product_name: string;
}

/** Name maps to >1 distinct CAS entities. Save blocked pending user selection. */
export interface PutProductMultiResult {
  status: 'multi_result';
  candidates: CasCandidate[];
  orphanCount?: number; // present for verified path; absent for unverified path
}

export type PutProductResult =
  PutProductSuccess | PutProductConflict | PutProductMultiResult;

// ─── POST /products/:id/fork ────────────────────────────────────────────────

export interface ForkProductRequest {
  manufacturerId: number;
  conflictingEntity: { rn: string; name: string };
  newManProductName?: string;
}

export interface ForkProductResponse {
  new_product_id: number;
  original_deleted: boolean;
}

// ─── POST /products/:id/fork-all ─────────────────────────────────────────────

export interface ForkAllProductRequest {
  manufacturerId: number;
  candidates: CasCandidate[];
}

export interface ForkAllProductResponse {
  new_product_ids: number[];
  original_deleted: boolean;
}

export interface ProductsQueryParams {
  search?: string;
  advancedSearch?: AdvancedSearchQuery;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
}

// File upload types
export interface ProductUploadRequest {
  manufacturer_ids: number[];
  facility_ids: number[];
  file: File;
}

// Upload differences structure based on uploadProducts.js analysis
export interface ExistingProductRecord {
  product_id: number;
  product_cas_name: string;
  cas_number?: string;
  note?: string;
  manufacturers: ManufacturerSummary[];
  facilities: FacilitySummary[];
  grades: GradeSummary[];
  therapeutic_areas: TherapeuticAreaSummary[];
}

export interface UploadedProductRecord {
  'PRODUCT NAME': string;
  'CAS NUMBER'?: string;
  GRADE?: string;
  'THERAPEUTIC AREA'?: string;
  NOTE?: string;
  __rowNum: number; // Excel row number for error reporting
}

export interface ProductChangeRecord {
  before: ExistingProductRecord;
  after: UploadedProductRecord;
}

export interface UploadDifferences {
  new: UploadedProductRecord[];
  changed: ProductChangeRecord[];
  deleted: ExistingProductRecord[];
}

export interface ProductUploadResponse {
  hasDifferences: boolean;
  differences?: UploadDifferences;
  success?: boolean;
  message?: string;
  insertedCount?: number;
}

// Legacy type - keeping for backward compatibility
export interface ProductDifference {
  type: 'add' | 'update' | 'delete';
  oldValue?: string;
  newValue?: string;
  product_id?: number;
}

export interface CommitChangesRequest {
  manufacturer_ids: number[];
  facility_ids: number[];
  differences: UploadDifferences;
}

// GET /products/name-precedence-config — one entry per dbo.Name_Precedence_Config
// row, ordered by rank. Not grouped/collapsed: cas_official appears once per
// confirmed source (fda_gsrs/pubchem/common_chemistry), each its own step.
export interface NamePrecedenceStep {
  step: number;
  name_role: string;
  source_system: string;
  label: string;
}

// GET /products/:id/name-variants — every active dbo.Product_Names row for a
// product, ranked via dbo.Name_Precedence_Config. rank is null for rows with
// no config match (e.g. cas_synonym); those always sort last.
//
// source_record_id/owner added in issue #622 Sub-task 5.3 — source_record_id
// is the source table's own primary key (null for rows with no source
// record, e.g. cas_synonym), and owner is the responsible party for an
// externally-sourced name (Orange Book applicant_full_name / NDC
// labeler_name / USDMF holder) or the manufacturer's company name for the
// manufacturer role; null for roles with no owner concept
// (cas_official/preferred/cas_synonym).
export interface ProductNameVariant {
  name_id: number;
  name: string;
  name_role: string;
  source_system: string;
  source_record_id: string | null;
  rank: number | null;
  label: string;
  is_synonym: boolean;
  owner: string | null;
}
